/* eslint-disable react-refresh/only-export-components */
import { createRouter, createRoute, createRootRoute, Outlet, Navigate } from '@tanstack/react-router';
import { isStandalone } from '@/shared/pwa/installed';
import { clearSignupAfterInstall, standaloneSignedOutRoute } from '@/shared/pwa/installFirst';
import { Suspense, lazy } from 'react';
import { officePage } from '@/shared/office/officePage';
import { useAuth } from '@/shared/hooks/useAuth';
import { ProtectedRoute } from '@/shared/components/ProtectedRoute';
import { OrgsUnavailable } from '@/shared/components/OrgsUnavailable';
import { EntitledRoute } from '@/shared/components/EntitledRoute';
import { ROUTE_FEATURE } from './routes/routeFeature';
import { AppLayout } from '@/shared/layouts/AppLayout';
// taxregime's trace Back button: a lazy chunk, nothing in the first load until someone traces a figure (check:bundle)
const TraceBackSlot = lazy(() => import('@/shared/trace/TraceBackSlot').then((m) => ({ default: m.TraceBackSlot })));
import { RouteError } from '@/shared/components/RouteError';
import { SelfHealingNotFound } from '@/shared/components/SelfHealingNotFound';
import { postLoginRoute } from '@/shared/config/postLoginLanding';
import { hasAuthLinkError } from '@/shared/auth/linkError';
import { shouldOfferPassword } from '@/shared/auth/passwordPrompt';
import { buildSiteRoutes } from '@/site/routes';
import { SiteRootPage } from '@/site/SiteShell';
import { isHealthHost, isHospitalLogin } from '@/shared/hms/host';
import { isAgentHost, isBrandHost, isTravelHost, isTravelLogin } from '@/shared/travel/host';

// ─────────────────────────────────────────────
// Lazy-loaded routes (code splitting)
// ─────────────────────────────────────────────

// Dashboard is eager - it's the main app page
import Dashboard from './routes/Dashboard';

// Marketing
const PartnersPage = lazy(() => import('./routes/marketing/PartnersPage'));
const PortalLogin = lazy(() => import('./routes/PortalLogin'));
const LegalPage = lazy(() => import('./routes/marketing/LegalPage'));

// Auth - small pages, still lazy
const SignUp = lazy(() => import('./routes/auth/SignUp'));
const SignIn = lazy(() => import('./routes/auth/SignIn'));
const SetPassword = lazy(() => import('./routes/auth/SetPassword'));
const ResetPassword = lazy(() => import('./routes/auth/ResetPassword'));
const Onboarding = lazy(() => import('./routes/Onboarding'));
const CreatePractice = lazy(() => import('./routes/CreatePractice'));
const InviteAccept = lazy(() => import('./routes/InviteAccept'));
const EmployeeInvite = lazy(() => import('./routes/EmployeeInvite'));
const Offer = lazy(() => import('./routes/Offer'));
const MyWorkplace = lazy(() => import('./routes/MyWorkplace'));
const GstReconnect = lazy(() => import('./routes/GstReconnect'));
const AuditorConfirm = lazy(() => import('./routes/AuditorConfirm'));
const ShortLink = lazy(() => import('./routes/ShortLink'));
const GstApprove = lazy(() => import('./routes/GstApprove'));
const TdsApprove = lazy(() => import('./routes/TdsApprove'));
const TdsCsiOtp = lazy(() => import('./routes/TdsCsiOtp'));
const VerifyExecution = lazy(() => import('./routes/VerifyExecution'));
const SignByLink = lazy(() => import('./routes/SignByLink'));
const MeetByLink = lazy(() => import('./routes/MeetByLink'));
const SignatureByLink = lazy(() => import('./routes/SignatureByLink'));
const TdsChallanUpload = lazy(() => import('./routes/TdsChallanUpload'));
const ShareCertificateOpen = lazy(() => import('./routes/ShareCertificateOpen'));
const GstPacketReviewPage = lazy(() => import('./routes/GstPacketReviewPage'));
const LendPack = lazy(() => import('./routes/LendPack'));
const ItrApprove = lazy(() => import('./routes/ItrApprove'));
const ShareRoom = lazy(() => import('./routes/ShareRoom'));
const ClientChat = lazy(() => import('./routes/ClientChat'));

// Core Accounting
const ChartOfAccounts = lazy(() => import('./routes/ChartOfAccounts'));
const OpeningBalancesWizard = lazy(() => import('./routes/OpeningBalancesWizard'));
const GetStarted = lazy(() => import('./routes/GetStarted'));
const Contacts = lazy(() => import('./routes/Contacts'));
const Items = lazy(() => import('./routes/Items'));
const Journals = lazy(() => import('./routes/Journals'));

// Sales
const Sales = lazy(() => import('./routes/Sales'));
const Estimates = lazy(() => import('./routes/Estimates'));
const CreditNotes = lazy(() => import('./routes/CreditNotes'));
const DeliveryChallans = lazy(() => import('./routes/DeliveryChallans'));
const RecurringInvoices = lazy(() => import('./routes/RecurringInvoices'));

// Purchases
const Purchases = lazy(() => import('./routes/Purchases'));
const PurchaseOrders = lazy(() => import('./routes/PurchaseOrders'));
const DebitNotes = lazy(() => import('./routes/DebitNotes'));
const GoodsReceivedNotes = lazy(() => import('./routes/GoodsReceivedNotes'));
const RecurringBills = lazy(() => import('./routes/RecurringBills'));

// Payments & Banking
const MoneyIn = lazy(() => import('./routes/MoneyIn'));
const MoneyOut = lazy(() => import('./routes/MoneyOut'));
const Transfers = lazy(() => import('./routes/Transfers'));
const Expenses = lazy(() => import('./routes/Expenses'));
const BankReconciliation = lazy(() => import('./routes/BankReconciliation'));
const BankStatements = lazy(() => import('./routes/BankStatements'));

// Reports
const Reports = lazy(() => import('./routes/Reports'));
const ReportBuilder = lazy(() => import('./routes/ReportBuilder'));
const ComplianceReports = lazy(() => import('./routes/ComplianceReports'));
const NGOReports = lazy(() => import('./routes/NGOReports'));
const Donations = lazy(() => import('./routes/Donations'));
// officecreds: every client's portal logins, out as one CSV and back in (office admins).
const OfficeCredentials = lazy(() => import('./routes/OfficeCredentials'));
const OfficeDsc = lazy(() => import('./routes/OfficeDsc'));
const OfficeFilings = lazy(() => import('./routes/OfficeFilings'));
const OfficeTds = lazy(() => import('./routes/OfficeTds'));
// practice-1: Karr Practice's desk, a tab of the Office (docs/KARR_PRACTICE_SPEC.md).
const PracticeMatters = lazy(() => import('./routes/practice/PracticeMatters'));
// clientsheet: every number and portal login of a client, on one page.
const ClientSheet = lazy(() => import('./routes/ClientSheet'));
const AdvanceTax = lazy(() => import('./routes/AdvanceTax'));
const AccrualCockpit = lazy(() => import('./routes/AccrualCockpit'));
const MyBusinesses = lazy(() => import('./routes/MyBusinesses'));

// Modules
const FixedAssets = lazy(() => import('./routes/FixedAssets'));
const AddAssetFlow = lazy(() => import('./routes/fixedAssets/AddAssetFlow'));
const AssetJourney = lazy(() => import('./routes/fixedAssets/AssetJourney'));
const Payroll = lazy(() => import('./routes/Payroll'));
const Inventory = lazy(() => import('./routes/Inventory'));
const SerialLife = lazy(() => import('./routes/inventory/SerialLife'));
const Manufacturing = lazy(() => import('./routes/Manufacturing'));
const Approvals = lazy(() => import('./routes/Approvals'));
const SalesOrders = lazy(() => import('./routes/SalesOrders'));
const Projects = lazy(() => import('./routes/Projects'));
// build-1: Karr Build's Projects desk, in Karr's own shell (lazy chunk).
const BuildHome = lazy(() => import('./routes/build/BuildHome'));
const POS =lazy(() => import('./routes/POS'));
const RestaurantPOS = lazy(() => import('./routes/RestaurantPOS'));
const KitchenDisplay = lazy(() => import('./routes/KitchenDisplay'));
const FoodCost = lazy(() => import('./routes/FoodCost'));
const RFQ = lazy(() => import('./routes/RFQ'));
const HR = lazy(() => import('./routes/HR'));
const Helpdesk = lazy(() => import('./routes/Helpdesk'));
const QMS = lazy(() => import('./routes/QMS'));
const Maintenance = lazy(() => import('./routes/Maintenance'));
// fleet-1: Karr Fleet, a lazy chunk in Karr's own shell.
const FleetHome = lazy(() => import('./routes/fleet/FleetHome'));
// mandi-1: Karr Mandi, a lazy chunk in Karr's own shell.
const MandiHome = lazy(() => import('./routes/mandi/MandiHome'));
// stay-1: Karr Stay, a lazy chunk in Karr's own shell.
const StayHome = lazy(() => import('./routes/stay/StayHome'));
// campus-1: Karr Campus, a lazy chunk in Karr's own shell.
const CampusHome = lazy(() => import('./routes/campus/CampusHome'));
// jewel-1: Karr Jewel, a lazy chunk in Karr's own shell.
const JewelHome = lazy(() => import('./routes/jewel/JewelHome'));
// studio-1: Karr Studio, its seven desks one lazy chunk in Karr's own shell.
const StudioHome = lazy(() => import('./routes/studio/StudioHome'));
// service-1: Karr Service, a lazy chunk in Karr's own shell.
const ServiceHome = lazy(() => import('./routes/service/ServiceHome'));
const Dimensions = lazy(() => import('./routes/Dimensions'));
const FixedExpenses = lazy(() => import('./routes/FixedExpenses'));
const Ecommerce = lazy(() => import('./routes/Ecommerce'));
const Documents = lazy(() => import('./routes/Documents'));
const Subscriptions = lazy(() => import('./routes/Subscriptions'));
const Budgeting = lazy(() => import('./routes/Budgeting'));
const Projections = lazy(() => import('./routes/Projections'));
// me-1: Karr Me, the person's own year (docs/KARR_ME_SPEC.md); a lazy chunk.
const MeHome = lazy(() => import('./routes/me/MeHome'));
const TimeTracking = lazy(() => import('./routes/TimeTracking'));
const MultiCurrency = lazy(() => import('./routes/MultiCurrency'));
const CustomerVendorPortal = lazy(() => import('./routes/CustomerVendorPortal'));
const AiTools = lazy(() => import('./routes/AiTools'));
const Compliance = lazy(() => import('./routes/Compliance'));
const Modules = lazy(() => import('./routes/Modules'));
const Marketplace = lazy(() => import('./routes/Marketplace'));
const PayWeek = lazy(() => import('./routes/PayWeek'));
const NetworkConnect = lazy(() => import('./routes/NetworkConnect'));
const BooksHealth = lazy(() => import('./routes/BooksHealth'));
const YearTies = lazy(() => import('./routes/YearTies'));
const Notices = lazy(() => import('./routes/Notices'));
const BooksHealthAdmin = lazy(() => import('./routes/admin/BooksHealthAdmin'));
const ReleasesAdmin = lazy(() => import('./routes/admin/Releases'));
const PlacesAdmin = lazy(() => import('./routes/admin/Places'));
const PlaceCloudReturn = lazy(() => import('./routes/place/CloudReturn'));
const BoxClaim = lazy(() => import('./routes/box/Claim'));
const CustomersAdmin = lazy(() => import('./routes/admin/CustomersAdmin'));
const ComplianceRuleAdmin = lazy(() => import('./routes/admin/ComplianceRuleAdmin'));
const AdminHome = lazy(() => import('./routes/admin/AdminHome'));
const ErrorLog = lazy(() => import('./routes/admin/ErrorLog'));
const IdeasAdmin = lazy(() => import('./routes/admin/IdeasAdmin'));
const AccountingScore = lazy(() => import('./routes/AccountingScore'));
const LoanManagement = lazy(() => import('./routes/LoanManagement'));
const CRM = lazy(() => import('./routes/CRM'));
const TaskManagement = lazy(() => import('./routes/TaskManagement'));
// warmtabs: the Office pages fetch each other's code and, once here, draw
// with no loading flash (shared/office/officePage).
const PortfolioCockpit = officePage(() => import('./routes/PortfolioCockpit'));
const PortfolioClient = lazy(() => import('./routes/PortfolioClient'));
const MyWork = lazy(() => import('./routes/MyWork'));
const DocumentRequests = officePage(() => import('./routes/DocumentRequests'));
const ProductCodes = lazy(() => import('./routes/ProductCodes'));
const PracticeCRM = officePage(() => import('./routes/PracticeCRM'));
const PartnerProgram = officePage(() => import('./routes/PartnerProgram'));
const Notifications = lazy(() => import('./routes/Notifications'));
const GstTds = lazy(() => import('./routes/GstTds'));
const GstFiling = lazy(() => import('./routes/GstFiling'));
const Softex = lazy(() => import('./routes/Softex'));
const ItrFiling = lazy(() => import('./routes/ItrFiling'));
const TdsFiling = lazy(() => import('./routes/TdsFiling'));
const PayrollFiling = lazy(() => import('./routes/PayrollFiling'));
const StatutoryRegisters = lazy(() => import('./routes/StatutoryRegisters'));
const RocJourney = lazy(() => import('./routes/RocJourney'));
const CopilotGet = lazy(() => import('./routes/CopilotGet'));
const CopilotFiled = lazy(() => import('./routes/CopilotFiled'));
const HmsHome = lazy(() => import('./routes/hms/HmsHome'));
// hmsux A-1: a visit and its bill as a screen of its own (a lazy chunk).
const HmsVisitScreen = lazy(() => import('./routes/hms/VisitScreen'));
const TravelHome = lazy(() => import('./routes/travel/TravelHome'));
// ts-2: the travel company's own shell and its control room (lazy chunks; the accounting shell is never drawn there).
const TravelArea = lazy(() => import('./routes/travel/TravelArea'));
const TravelHq = lazy(() => import('./routes/travel/HqPage'));
// ts-4: the travel people directory.
const TravelPeople = lazy(() => import('./routes/travel/PeoplePage'));
// ts-6: the travel company's own door, the PIN, its tablets.
const TravelSignIn = lazy(() => import('./routes/travel/TravelSignIn'));
const TravelPin = lazy(() => import('./routes/travel/TravelPin'));
const TravelDevices = lazy(() => import('./routes/travel/DevicesPage'));
const TravelAskPublic = lazy(() => import('./routes/travel/AskPublic'));
const TravelDepartureLanding = lazy(() => import('./routes/travel/DepartureLanding'));
// te-31: Karr's status page (PUBLIC, no login).
const PlatformStatusPage = lazy(() => import('./routes/PlatformStatus'));
const SecurityDisclosurePage = lazy(() => import('./routes/SecurityDisclosure'));
const TravelItineraryPublic = lazy(() => import('./routes/travel/ItineraryPublic'));
const TravelPartnerPublic = lazy(() => import('./routes/travel/PartnerPublic'));
// ts-24: the agent site, outside Karr's shell; an agent is never a seat.
const TravelAgentApp = lazy(() => import('./routes/travel/AgentApp'));
// ts-28: a company's own booking page for its employees.
const TravelCorporateBook = lazy(() => import('./routes/travel/CorporateBook'));
// ts-30: each brand's own booking page.
const TravelGoPage = lazy(() => import('./routes/travel/GoPage'));
const TravelApprovalPublic = lazy(() => import('./routes/travel/ApprovalPublic'));
const TravelCompanyPublic = lazy(() => import('./routes/travel/CompanyPublic'));
// practice-8: THE party page, a k token on the /p door (any vertical's party).
const PartyPage = lazy(() => import('./routes/public/PartyPage'));
// campus-17: a school's admission form and one application, no login (/apply/a… and /apply/b…).
const ApplyPage = lazy(() => import('./routes/public/ApplyPage'));
// stay-7: a hotel's own booking page and one booking's page, no login (/stay/<name>, /stay/b/s…).
const StayPublicPage = lazy(() => import('./routes/public/StayPage'));
// studio-5: a studio's own booking page and one booking's page, no login (/studio/<name>, /studio/b/t…).
const StudioPublicPage = lazy(() => import('./routes/public/StudioPage'));
const StudioDoorPage = lazy(() => import('./routes/public/StudioDoor'));
const GivePage = lazy(() => import('./routes/public/GivePage'));
const ProposalPage = lazy(() => import('./routes/public/ProposalPage'));
const HmsBookingPage = lazy(() => import('./routes/hms/BookingPage'));
const HmsPatientDocPage = lazy(() => import('./routes/hms/PatientDocPage'));
// hf-3: the hospital's own door (a badge number and a PIN), the PIN change, the device page.
const HealthSignIn = lazy(() => import('./routes/health/HealthSignIn'));
const HealthPin = lazy(() => import('./routes/health/HealthPin'));
const HealthDevice = lazy(() => import('./routes/health/HealthDevice'));
// hf-12: a department's display board on a corridor TV (PUBLIC: a registered display device, no person).
const HealthDisplay = lazy(() => import('./routes/health/HealthDisplay'));
// hf-4: the hospital's people directory (owners, admins, HR).
const HealthPeople = lazy(() => import('./routes/health/PeoplePage'));
// hf-15: the duty roster on Karr Care's own tables (HR, owners, admins, the MS; a nurse in charge for her ward).
const HealthRoster = lazy(() => import('./routes/health/RosterPage'));
// hf-17: where Google or Microsoft hands a hospital person back.
const HealthSso = lazy(() => import('./routes/health/HealthSso'));
// hf-fix-3: Health in Karr's own shell; the area hands the sidebar the person's desks (a lazy chunk).
const HealthArea = lazy(() => import('./routes/health/HealthArea'));
const AuditTrail = lazy(() => import('./routes/AuditTrail'));
const Audit = lazy(() => import('./routes/Audit'));
const AuditRedirect = lazy(() => import('./routes/AuditRedirect'));
const MyAudits = lazy(() => import('./routes/MyAudits'));
const PeriodLocks = lazy(() => import('./routes/PeriodLocks'));
const Receivables = lazy(() => import('./routes/Receivables'));
const Consolidation = lazy(() => import('./routes/Consolidation'));

// Settings & Config
const Developers = lazy(() => import('./routes/Developers'));
const Integrations = lazy(() => import('./routes/Integrations'));
const ImportData = lazy(() => import('./routes/ImportData'));
const VoucherImport = lazy(() => import('./routes/VoucherImport'));
const TallyImport = lazy(() => import('./routes/TallyImport'));
const DocumentImport = lazy(() => import('./routes/DocumentImport'));
const PlatformImport = lazy(() => import('./routes/PlatformImport'));
const PlatformReview = lazy(() => import('./routes/PlatformReview'));
const PlatformFira = lazy(() => import('./routes/PlatformFira'));
const PlatformExports = lazy(() => import('./routes/PlatformExports'));
const PlatformSubscriptions = lazy(() => import('./routes/PlatformSubscriptions'));
const GstRefund = lazy(() => import('./routes/GstRefund'));
const PlatformStatement = lazy(() => import('./routes/PlatformStatement'));
const PlatformInbox = lazy(() => import('./routes/PlatformInbox'));
const ConnectDone = lazy(() => import('./routes/ConnectDone'));
const Collaboration = lazy(() => import('./routes/Collaboration'));
// aiconsent ac-5: the consent page and one record, read in full.
const AiAccessConsent = lazy(() => import('./routes/AiAccessConsent'));
const AiAccessRecord = lazy(() => import('./routes/AiAccessRecord'));

// Feature Modules (Phase 2-5)
const PaymentLinks = lazy(() => import('./routes/PaymentLinks'));
const EmailIntegration = lazy(() => import('./routes/EmailIntegration'));
const ReceiptScanner = lazy(() => import('./routes/ReceiptScanner'));
// practice-12: where a share to Karr lands (public/share-sw.js), and goes on by what it is.
const Share = lazy(() => import('./routes/Share'));
const BankFeeds = lazy(() => import('./routes/BankFeeds'));
const KarrAgents = lazy(() => import('./routes/KarrAgents'));
const SettingsPage = lazy(() => import('./routes/Settings'));
const EmailOAuthCallback = lazy(() => import('./routes/EmailOAuthCallback'));

// ─────────────────────────────────────────────
// Content-area Suspense fallback
// ─────────────────────────────────────────────
// This renders INSIDE AppLayout — the sidebar & header
// stay mounted. Only the content area shows this skeleton.

function ContentFallback() {
    return (
        <div className="p-4 sm:p-6 flex-1 relative z-[1]">
            <div className="space-y-4 max-w-5xl animate-in fade-in duration-200">
                <div className="h-7 w-48 bg-stone-200/60 dark:bg-stone-700/40 rounded-lg animate-pulse" />
                <div className="h-4 w-80 bg-stone-100/80 dark:bg-stone-800/40 rounded animate-pulse" style={{ animationDelay: '75ms' }} />
                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-28 bg-stone-100/60 dark:bg-stone-800/30 rounded-xl animate-pulse" style={{ animationDelay: `${i * 100}ms` }} />
                    ))}
                </div>
                <div className="mt-4 h-64 bg-stone-100/60 dark:bg-stone-800/30 rounded-xl animate-pulse" style={{ animationDelay: '400ms' }} />
            </div>
        </div>
    );
}

/** Wrap a lazy component in Suspense (for non-layout routes like auth, landing) */
function L({ children }: { children: React.ReactNode }) {
    return <Suspense fallback={<ContentFallback />}>{children}</Suspense>;
}

// ─────────────────────────────────────────────
// Root route
// ─────────────────────────────────────────────
const rootRoute = createRootRoute({
    component: () => <Outlet />,
});

// ─────────────────────────────────────────────
// ★ PERSISTENT LAYOUT ROUTE ★
// ─────────────────────────────────────────────
// This is the key to smooth transitions like Zoho.
// AppLayout (sidebar + header) renders ONCE here and
// stays mounted across ALL page navigations.
// Only the <Outlet /> inside the content area changes.
// The <Suspense> boundary is INSIDE AppLayout's content
// area, so the sidebar/header never unmount/flash.
// ─────────────────────────────────────────────
const appLayoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: '_appLayout',
    component: function PersistentAppLayout() {
        // sidewin: inside one of Karr's floating windows this is the same
        // AppLayout, which knows it is in a window and draws only the sidebar
        // and the page (see `IN_WINDOW` there).
        return (
            // ts-6: on a travel brand's own domain the door is the travel sign-in;
            // hf-fix-3: on the hospital's, or on a Health page, the hospital's.
            <ProtectedRoute signIn={isTravelHost() ? '/travel/signin' : isHealthHost() || window.location.pathname.startsWith('/health') ? '/health/signin' : undefined}>
                <AppLayout>
                    <Suspense fallback={<ContentFallback />}>
                        <Outlet />
                    </Suspense>
                    <Suspense fallback={null}><TraceBackSlot /></Suspense>
                </AppLayout>
            </ProtectedRoute>
        );
    },
});

const ReferralWelcomeModal = lazy(() => import('@/shared/components/ReferralWelcomeModal').then((m) => ({ default: m.ReferralWelcomeModal })));

// ─── Landing Page (public — the site's home for guests, redirects auth users)
const landingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    component: function SmartLanding() {
        const { session, user, loading, orgs, orgsUnreadable } = useAuth();
        if (loading) return null;
        if (session) {
            // installfirst: signed in, so the install-then-sign-up trip is over.
            clearSignupAfterInstall();
            // Resume a pending invite stashed before sign-in (OAuth/OTP only
            // redirects back to origin '/', so we bounce to the invite here).
            let pending: string | null = null;
            try { pending = localStorage.getItem('pendingInviteToken'); } catch { /* ignore */ }
            if (pending) {
                try { localStorage.removeItem('pendingInviteToken'); } catch { /* ignore */ }
                return <Navigate to="/invite/$token" params={{ token: pending }} />;
            }
            // Same resume, for an employee self-service link (em-10).
            let pendingEmp: string | null = null;
            try { pendingEmp = localStorage.getItem('pendingEmployeeInviteToken'); } catch { /* ignore */ }
            if (pendingEmp) {
                try { localStorage.removeItem('pendingEmployeeInviteToken'); } catch { /* ignore */ }
                return <Navigate to="/employee-invite/$token" params={{ token: pendingEmp }} />;
            }
            // hf-6: on the hospital's address, and for a hospital login anywhere,
            // the hospital is home; the books are one press away for those who keep them.
            if (isHealthHost() || isHospitalLogin(user)) return <Navigate to="/health" />;
            // ts-2: on travel.karr.pro or a brand's own domain, and for a travel staff
            // login anywhere, the travel company is home.
            // ts-24: agents.<brand> is the agent site.
            if (isAgentHost()) return <Navigate to="/agent" />;
            if (isTravelHost() || isTravelLogin(user)) return <Navigate to="/travel" />;
            // Everything else about where they land is a pure function of the
            // memberships (ob-4) — no membership at all → My Workplace, all-staff
            // → their work queue, a practice of their own → the client roster
            // (O8, ob-5), two or more businesses of their own → the group view
            // (O7), otherwise the books. Kept in `postLoginLanding.ts` so every
            // branch is testable; it used to be four untested `if`s here, and the
            // O7 branch was the one that never got written.
            //
            // One interruption sits ahead of that, and only ever once: somebody who
            // got in by emailed link and has no password yet is offered one, so
            // their next sign-in doesn't depend on an email arriving. Below the
            // invite resumes on purpose — never interrupt someone mid-invite; they
            // get the offer the next time they land here.
            if (shouldOfferPassword(user)) return <Navigate to="/auth/set-password" />;
            // CAREFUL. `postLoginRoute` reads an empty list as "no membership at all"
            // and forwards to My Workplace, which forwards again to /onboarding.
            // That chain is correct for a brand-new account and a lie for anyone
            // else, so it may only run on a list we actually READ. When the read
            // failed, say so here rather than three redirects later.
            if (orgsUnreadable) return <L><OrgsUnavailable reason={orgsUnreadable} /></L>;
            return <Navigate to={postLoginRoute(orgs)} />;
        }
        // No session AND a sign-in link that failed: send them somewhere that can
        // explain it. Without this they get the marketing page and no reason —
        // the exact bug reported on 2026-08-01. See shared/auth/linkError.ts.
        if (hasAuthLinkError()) return <Navigate to="/auth/signin" />;
        // hf-6: the hospital's address opens on its own door, never the marketing site.
        if (isHealthHost()) return <Navigate to="/health/signin" />;
        // ts-2, ts-6: a travel address opens on its own door, never the marketing site.
        if (isAgentHost()) return <Navigate to="/agent" />;
        // ts-30: a brand's own public domain opens on its booking page; staff sign in at /travel/signin.
        if (isBrandHost(window.location.hostname)) return <Navigate to="/go" />;
        if (isTravelHost()) return <Navigate to="/travel/signin" />;
        // staylogged: the home-screen icon is the app, never the brochure. On an
        // iPhone the icon keeps its own storage apart from Safari, so its first
        // open has no session; showing the marketing page there read as "Karr
        // logged me out" (3 Oct 2026). Sign in once here and it stays.
        // installfirst: unless they installed from the sign-up screen, which
        // Android remembers across Chrome and the icon; then it opens on sign-up.
        if (isStandalone()) return <Navigate to={standaloneSignedOutRoute()} />;
        // sitetour-12 (4 Oct 2026): a guest gets the new site's home, which picks
        // the country it remembers, else the time zone's, else India.
        return <SiteRootPage extras={<ReferralWelcomeModal />} />;
    },
});

// ─── Auth Routes (public, NOT under layout) ───────────────────
const signUpRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/auth/signup',
    component: () => <L><SignUp /></L>,
});

const signInRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/auth/signin',
    // The email sign-in, on every address: an owner, admin or HR person on health.karr.pro
    // reaches it from the hospital's door ("Sign in with your email").
    component: () => <L><SignIn /></L>,
});

// Offered once after a first sign-in by link (see shared/auth/passwordPrompt.ts),
// and reachable any time from the account menu to change it.
//
// ⚠ Deliberately NOT wrapped in ProtectedRoute: that guard sends anyone with zero
// orgs to /onboarding, and a brand-new signup — the exact person this screen is
// for — has zero orgs at this moment. It would have redirected past itself every
// time. The screen carries its own session check instead.
const setPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/auth/set-password',
    component: () => <L><SetPassword /></L>,
});

// Where a "Forgot password?" email lands. Public: GoTrue establishes the session
// from the link itself, and the page has to be able to say so when it didn't.
const resetPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/auth/reset',
    component: () => <L><ResetPassword /></L>,
});

// ─── Partner Program landing (PUBLIC marketing) ──────────────
const partnersRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/partners',
    component: () => <L><PartnersPage /></L>,
});

// ─── Legal pages (PUBLIC — Terms / Privacy / Refund) ─────────
const legalRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/legal/$doc',
    component: () => <L><LegalPage /></L>,
});

// Public customer/vendor portal (password-gated, no app auth).
const portalLoginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/portal/$contactId',
    component: () => <L><PortalLogin /></L>,
});

// ─── Invite acceptance (PUBLIC — must render for logged-out invitees) ──
const inviteRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/invite/$token',
    component: () => <L><InviteAccept /></L>,
});

// ─── Employee self-service (em-10) ────────────────────────────────────
// Both sit OUTSIDE the app layout on purpose: an employee with a Karr sign-in
// is NOT a member of their employer's organisation, and ProtectedRoute sends
// anyone with no membership to /onboarding. They have no books to protect —
// they have one employee record, and the get_my_* RPCs guard it server-side.
const employeeInviteRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/employee-invite/$token',
    component: () => <L><EmployeeInvite /></L>,
});

// ─── The offer a candidate was sent (em-13) ───────────────────────────
// PUBLIC, and more so than the rest: an invitee at least has a Karr sign-in to
// connect. Whoever opens this has no account, no membership and no reason to
// make one until they have decided whether to take the job. Guarded entirely by
// the unguessable token in the path.
const offerRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/offer/$token',
    component: () => <L><Offer /></L>,
});

const myWorkplaceRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/my-workplace',
    component: () => <L><MyWorkplace /></L>,
});

// ─── Short-link redirector (PUBLIC — every minted client link forwards here) ──
const shortLinkRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/r/$slug',
    component: () => <L><ShortLink /></L>,
});

// ─── autoaud-6: the yearly auditor confirmation (PUBLIC — token-authorized link) ──
const auditorConfirmRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/auditor/confirm/$token',
    component: () => <L><AuditorConfirm /></L>,
});

// ─── GST portal reconnect (PUBLIC — taxpayer taps from a WhatsApp/email nudge) ──
const gstReconnectRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/gst/reconnect/$token',
    component: () => <L><GstReconnect /></L>,
});

// ─── GST filing brief approval (PUBLIC — client approves/queries from a link) ──
const gstApproveRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/gst/approve/$token',
    component: () => <L><GstApprove /></L>,
});

// ─── TDS statement approval (PUBLIC — the deductor approves from a link; tf-5) ──
const tdsApproveRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/tds/approve/$token',
    component: () => <L><TdsApprove /></L>,
});

// ─── Verify an executed set of financial statements (PUBLIC — the reader is
//     a bank or a department holding the document; ink-8). Two doors: the
//     certificate's link, and the printed reference for a paper copy. ──
const verifyExecutionRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/verify',
    component: () => <L><VerifyExecution /></L>,
});
const verifyExecutionTokenRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/verify/$token',
    component: () => <L><VerifyExecution /></L>,
});

// ─── Signing the financial statements (PUBLIC — a proprietor or director who
//     holds no Karr account signs from a link sent to their own channel, after
//     a one-time code proves it reached them; ink-5) ──
const signByLinkRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/sign/$token',
    component: () => <L><SignByLink /></L>,
});

// ─── Share certificates (PUBLIC — a director signs a batch of certificates
//     the way the statements are signed; certsign) ──
const signCertificatesRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/sign-certificates/$token',
    component: () => <L><SignByLink kind="certificates" /></L>,
});

// ─── Giving Karr your signature (PUBLIC: a person on a register who holds no
//     Karr account uploads their own mark, after a one-time code proves the
//     link reached them; sigreg). Nothing is signed here. ──
// ─── A company meeting (PUBLIC: a director or the chair, who holds no Karr
//     account, reads the notice, answers and, as chair, calls and confirms it
//     from their own link; boardroom-4). Built for a phone first. ──
const meetByLinkRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/meet/$token',
    component: () => <L><MeetByLink /></L>,
});

const signatureByLinkRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/signature/$token',
    component: () => <L><SignatureByLink /></L>,
});

// ─── TDS challan verification (PUBLIC — whoever holds the TAN-registered
//     phone taps "send the code" and types the OTP; tf-2 relay, §9) ──
const tdsCsiOtpRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/tds/csi/$token',
    component: () => <L><TdsCsiOtp /></L>,
});

// ─── TDS challan capture (PUBLIC — whoever PAID the tax photographs the
//     counterfoil; Karr reads it. td-auto-1, docs/TDS_AUTOPILOT.md §3) ──
const tdsChallanUploadRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/tds/challan/$token',
    component: () => <L><TdsChallanUpload /></L>,
});

// ─── Share certificate (PUBLIC — the member opens the certificate the company
//     sent them; the token opens that one certificate while it is in force.
//     certsend) ──
const shareCertificateOpenRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/share-certificate/$token',
    component: () => <L><ShareCertificateOpen /></L>,
});

// ─── GST client review packet (PUBLIC — the client reviews, approves and files
//     their own return from a phone; sf-2/sf-3, docs/CLIENT_SELF_FILING.md) ──
// travel-3: a travel business's ask form (PUBLIC, no login; two doors only).
const travelAskRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/ask/$token',
    component: () => <L><TravelAskPublic /></L>,
});

// travel-rich-12: a fixed departure's own page (PUBLIC, no login; one read), by the
// ask link, and on a brand's own domain at /d/<id>.
const travelLandingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/ask/$token/d/$id',
    component: () => <L><TravelDepartureLanding /></L>,
});
const travelBrandLandingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/d/$id',
    component: () => <L><TravelDepartureLanding /></L>,
});

// te-31: the service levels beside what was measured (PUBLIC, no login; one read).
const platformStatusRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/status',
    component: () => <L><PlatformStatusPage /></L>,
});

// te-41: how to report a weakness (PUBLIC, no login, no read).
const securityDisclosureRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/security',
    component: () => <L><SecurityDisclosurePage /></L>,
});

// ts-24: a partner agent's own site (PUBLIC page; the agent signs in on it).
const travelAgentRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/agent',
    component: () => <L><TravelAgentApp /></L>,
});
const travelAgentDeepRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/agent/$',
    component: () => <L><TravelAgentApp /></L>,
});
// ts-30: a brand's own booking page on its own domain (PUBLIC, no login).
const travelGoRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/go',
    component: () => <L><TravelGoPage /></L>,
});
// ts-28: a company's employees book their own trips (PUBLIC page; a work-email code signs them in).
const travelCorporateBookRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/c/$token/book',
    component: () => <L><TravelCorporateBook /></L>,
});

// hms-8: a clinic's online booking page (PUBLIC, no login; two doors only).
const hmsBookingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/book/$slug',
    component: () => <L><HmsBookingPage /></L>,
});

// hf-3: the hospital's own sign-in (PUBLIC, no login: a badge number and a
// PIN, or a registered device's list of names). The PIN change and the device
// page need a session but no accounting shell: the person is a nurse at a
// tablet, and the shell is hf-6's HealthShell.
const healthSignInRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/health/signin',
    component: () => <L><HealthSignIn /></L>,
});
const healthPinRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/health/pin',
    component: function ProtectedHealthPin() {
        const { session, initialized, loading } = useAuth();
        if (loading) return null;
        if (!session && initialized) return <Navigate to="/health/signin" />;
        return <L><HealthPin /></L>;
    },
});
// hf-17: the group's own sign-in comes back here (a session, no shell yet).
const healthSsoRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/health/sso',
    component: () => <L><HealthSso /></L>,
});
const healthDisplayRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/health/display',
    component: () => <L><HealthDisplay /></L>,
});
const healthDisplayCodeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/health/display/$code',
    component: () => <L><HealthDisplay /></L>,
});

// hms-12: a patient opens one final document by its link (PUBLIC, no login).
// travel-4: the same /p/<token> door. A travel token is one letter that is
// NOT a hex digit and 64 hex: t the itinerary or trip pack (travel-4), p a
// partner agent's page (travel-11), v a company's approval and w a company's
// own page (travel-14), k a party's page (practice-8) and m one machine's page,
// the same party page (service-11). An HMS token is 64 hex, so it never starts with one.
const TRAVEL_DOORS: Record<string, React.ComponentType> = {
    t: TravelItineraryPublic, p: TravelPartnerPublic, v: TravelApprovalPublic, w: TravelCompanyPublic, k: PartyPage, m: PartyPage,
};
const hmsPatientDocRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/p/$token',
    component: function PublicLink() {
        const { token } = hmsPatientDocRoute.useParams();
        const Travel = /^[g-z][0-9a-f]{64}$/.test(token) ? TRAVEL_DOORS[token[0]] : undefined;
        return <L>{Travel ? <Travel /> : <HmsPatientDocPage />}</L>;
    },
});

const applyRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/apply/$token',
    component: () => <L><ApplyPage /></L>,
});

// ─── A hotel's own booking page (PUBLIC — the hotel's page name is the whole authority, stay-7) ──
const stayPublicRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/stay/$name',
    component: () => <L><StayPublicPage /></L>,
});
const stayBookingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/stay/b/$token',
    component: () => <L><StayPublicPage /></L>,
});

// ─── A studio's own booking page (PUBLIC — the studio's page name is the whole authority, studio-5) ──
const studioPublicRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/studio/$name',
    component: () => <L><StudioPublicPage /></L>,
});
const studioBookingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/studio/b/$token',
    component: () => <L><StudioPublicPage /></L>,
});
// studio-11: the door's QR opens this on a client's phone (PUBLIC — the door token and the phone's own party page link).
const studioDoorRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/studio/door/$door',
    component: () => <L><StudioDoorPage /></L>,
});

// ─── A trust's campaign page (PUBLIC — the campaign token is the whole authority, campus-20) ──
const givePageRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/give/$token',
    component: () => <L><GivePage /></L>,
});

// ─── A firm's proposal (PUBLIC — the client opens it from the link they were sent and
//     accepts, declines or asks; the token is the whole authority, prop-5) ──
// (/offer/$token is the candidate's job offer, em-13; a proposal has its own address.)
const proposalPageRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/proposal/$token',
    component: () => <L><ProposalPage /></L>,
});

const gstPacketReviewRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/gst/review/$token',
    component: () => <L><GstPacketReviewPage /></L>,
});

// ─── Lender pack (PUBLIC — consented share via one-time token, ks-3) ──
const lendPackRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/lend/pack/$token',
    component: () => <L><LendPack /></L>,
});

// ─── Screen share room (PUBLIC — the token is the invitation; ss-1,
//     docs/SCREEN_SHARE.md). Either side lands here to share or watch. ──
// ─── Client chat (PUBLIC — the token is the identity; cm-2,
//     docs/CLIENT_MESSAGING.md). A client talks to their accountant, no login. ──
const clientChatRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/chat/$token',
    component: () => <L><ClientChat /></L>,
});

const shareRoomRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/share/$token',
    component: () => <L><ShareRoom /></L>,
});

// ─── ITR filing brief approval (PUBLIC — client approves/queries from a link) ──
const itrApproveRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/itr/approve/$token',
    component: () => <L><ItrApprove /></L>,
});

// ─── Onboarding (authenticated, no layout — has its own UI) ───
const onboardingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/onboarding',
    component: function ProtectedOnboarding() {
        const { session, initialized, loading } = useAuth();
        if (loading) return null;
        if (!session && initialized) return <Navigate to="/auth/signin" />;
        return <L><Onboarding /></L>;
    }
});

// ─── Create Practice (authenticated, no layout) ────────
const createPracticeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/create-practice',
    component: function ProtectedCreatePractice() {
        const { session, initialized, loading } = useAuth();
        if (loading) return null;
        if (!session && initialized) return <Navigate to="/auth/signin" />;
        return <L><CreatePractice /></L>;
    }
});

// ═══════════════════════════════════════════════════════════════
// ALL ROUTES BELOW ARE CHILDREN OF appLayoutRoute
// → AppLayout stays mounted, only content area swaps
// → No white flash, no sidebar/header remounting
// ═══════════════════════════════════════════════════════════════

// ─── Dashboard ───────────────────────────────────────────────
const dashboardRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/dashboard',
    component: () => <Dashboard />,
});

// ─── Chart of Accounts (staff+) ─────────────────────────────
const accountsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/accounts',
    component: () => <L><ChartOfAccounts /></L>,
});

// ─── Opening / Starting Balances (guided, Simple-mode friendly) ──
const openingBalancesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/opening-balances',
    component: () => <L><OpeningBalancesWizard /></L>,
});

// ─── Get Started hub (six-step onboarding; disappears when complete) ──
const getStartedRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/get-started',
    component: () => <L><GetStarted /></L>,
});

// ─── Contacts (staff+) ─────────────────────────────────────
const contactsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/contacts',
    component: () => <L><Contacts /></L>,
});

// ─── Items (staff+) ─────────────────────────────────────────
const itemsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/items',
    component: () => <L><Items /></L>,
});

// ─── Journals (accountant+) ─────────────────────────────────
const journalsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/journals',
    component: () => <L><Journals /></L>,
});

// ─── Sales & Invoices (staff+) ──────────────────────────────
const salesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/sales',
    component: () => <L><Sales /></L>,
});

// ─── Purchases & Bills (staff+) ─────────────────────────────
const purchasesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/purchases',
    component: () => <L><Purchases /></L>,
});

// ─── Money In (staff+) ─────────────────────────────────────
const moneyInRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/money-in',
    component: () => <L><MoneyIn /></L>,
});

// ─── Money Out (staff+) ────────────────────────────────────
const moneyOutRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/money-out',
    component: () => <L><MoneyOut /></L>,
});

// ─── Transfers (accountant+) ───────────────────────────────
const transfersRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/transfers',
    component: () => <L><Transfers /></L>,
});

// ─── Before the bill: estimates, quotations and proformas (staff+) ──────
//
// NOT behind `EntitledRoute feature="estimates"` any more. Founder, 9 Sep 2026:
// "make sure that other options that we had earlier in estimates are back and
// free with the basic package". On a plan that lists `estimates: false` the whole
// page was replaced by an upgrade panel.
//
// It also had to go for a second reason the founder did not have to spell out:
// the PROFORMA lives on this page now, and it was never gated when it sat under
// /sales. Leaving the gate would have quietly put a document people already had
// behind a paywall.
//
// Nothing server-side enforced it: neither `create_estimate` nor
// `create_proforma_invoice` calls `require_entitlement`, so this was a UI gate
// only and removing it makes the page work end to end rather than fail on save.
const estimatesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/estimates',
    component: () => <L><Estimates /></L>,
});

// ─── Credit Notes (staff+) ───────────────────────────────
const creditNotesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/credit-notes',
    component: () => <L><EntitledRoute feature={ROUTE_FEATURE['/credit-notes']}><CreditNotes /></EntitledRoute></L>,
});

// ─── Delivery Challans (staff+) ──────────────────────────
const deliveryChallansRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/delivery-challans',
    component: () => <L><DeliveryChallans /></L>,
});

// ─── Recurring Invoices (staff+) ─────────────────────────
const recurringInvoicesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/recurring-invoices',
    component: () => <L><EntitledRoute feature={ROUTE_FEATURE['/recurring-invoices']}><RecurringInvoices /></EntitledRoute></L>,
});

// ─── Purchase Orders (staff+) ────────────────────────────
const purchaseOrdersRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/purchase-orders',
    component: () => <L><PurchaseOrders /></L>,
});

// ─── Debit Notes (staff+) ────────────────────────────────
const debitNotesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/debit-notes',
    component: () => <L><EntitledRoute feature={ROUTE_FEATURE['/debit-notes']}><DebitNotes /></EntitledRoute></L>,
});

// ─── Goods Received Notes (staff+) ───────────────────────
const grnRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/goods-received',
    component: () => <L><GoodsReceivedNotes /></L>,
});

// ─── Recurring Bills (staff+) ──────────────────────────
const recurringBillsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/recurring-bills',
    component: () => <L><EntitledRoute feature={ROUTE_FEATURE['/recurring-bills']}><RecurringBills /></EntitledRoute></L>,
});

// ─── Expenses (staff+) ──────────────────────────────────
const expensesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/expenses',
    component: () => <L><Expenses /></L>,
});

// ─── Reports (viewer+) ─────────────────────────────────
const reportsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/reports',
    component: () => <L><Reports /></L>,
});

// ─── GST Returns (retired) → the GST Filing Cockpit ────
// Recomputed GSTR-1/3B client-side from tax_lines, org-wide and GSTIN-blind, so a
// multi-GSTIN org read a wrong return. The cockpit builds the same returns from the
// per-registration server builders (build_gstr1/build_gstr3b) and can actually file.
const gstReportsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/gst-reports',
    component: () => <Navigate to="/gst-filing" replace />,
});

// ─── Bank Statements: the one place a statement comes in (bankhub) ───
const bankStatementsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/bank-statements',
    component: () => <L><BankStatements /></L>,
});

// ─── Bank Reconciliation (staff+) ──────────────────────
const bankReconciliationRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/bank-reconciliation',
    component: () => <L><BankReconciliation /></L>,
});

// ─── Fixed Assets (staff+) ─────────────────────────────────────
const fixedAssetsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/fixed-assets',
    component: () => <L><FixedAssets /></L>,
});
// far-3: add an asset, one question per screen on a phone (docs/FIXED_ASSETS_SPEC.md §5.1).
const addFixedAssetRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/fixed-assets/new',
    component: () => <L><AddAssetFlow /></L>,
});
// far-7: one asset's whole life, one call (docs/FIXED_ASSETS_SPEC.md §10.3).
const assetJourneyRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/fixed-assets/$assetId',
    component: () => <L><AssetJourney /></L>,
});

// ─── Payroll & HR (staff+) ──────────────────────────────────────
const payrollRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/payroll',
    component: () => <L><Payroll /></L>,
});

// ─── Inventory (staff+) ─────────────────────────────────────────
const inventoryRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/inventory',
    component: () => <L><EntitledRoute feature={ROUTE_FEATURE['/inventory']}><Inventory /></EntitledRoute></L>,
});
// inv-A3: one serial's life (bought, moved, sold, warranty).
const serialLifeRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/inventory/serial/$id',
    component: () => <L><EntitledRoute feature={ROUTE_FEATURE['/inventory']}><SerialLife /></EntitledRoute></L>,
});

// ─── Manufacturing / Production (staff+) ────────────────────────────
const manufacturingRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/manufacturing',
    component: () => <L><EntitledRoute feature={ROUTE_FEATURE['/manufacturing']}><Manufacturing /></EntitledRoute></L>,
});

// ─── Approvals / Workflow (staff+) ──────────────────────────────────
const approvalsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/approvals',
    component: () => <L><Approvals /></L>,
});

// ─── Sales Orders (staff+) ──────────────────────────────────────────
const salesOrdersRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/sales-orders',
    component: () => <L><SalesOrders /></L>,
});

// ─── Projects / Job Costing (staff+) ────────────────────────────────
const projectsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/projects',
    component: () => <L><Projects /></L>,
});

// ─── Karr Build (build-1): the module's desks, in Karr's own shell ──
const buildRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/build',
    component: () => <L><BuildHome /></L>,
});

// ─── Point of Sale (staff+) ─────────────────────────────────────────
const posRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/pos',
    component: () => <L><POS /></L>,
});

// ─── Restaurant POS — dine-in floor (staff+) ────────────────────────
const restaurantRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/restaurant',
    component: () => <L><RestaurantPOS /></L>,
});

const kdsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/kds',
    component: () => <L><KitchenDisplay /></L>,
});

const foodCostRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/food-cost',
    component: () => <L><FoodCost /></L>,
});

// ─── Delivery apps: folded into the platforms home (kp-5, 23 Oct 2026) ──
// Zomato and Swiggy statements are booked there (sales, commission with its
// ITC, TCS, TDS, the payout) by post_platform_settlement (mktin-2); the kp-5
// scaffold kept a second, books-free list of the same orders. An old link lands there.
const aggregatorsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/aggregators',
    component: () => <Navigate to="/ecommerce" replace />,
});

// ─── Warehouse Operations: folded into Inventory, Count (stockhome-4) ──
// Its transfers and counts live in /inventory now; an old link lands there.
const warehouseOpsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/warehouse-ops',
    component: () => <Navigate to="/inventory" hash="count" replace />,
});

// ─── RFQ / Procurement (staff+) ─────────────────────────────────────
const rfqRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/rfq',
    component: () => <L><RFQ /></L>,
});

// ─── HR (staff+) ────────────────────────────────────────────────────
const hrRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/hr',
    component: () => <L><HR /></L>,
});

// ─── Helpdesk (staff+) ──────────────────────────────────────────────
const helpdeskRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/helpdesk',
    component: () => <L><Helpdesk /></L>,
});

// ─── Quality (staff+) ───────────────────────────────────────────────
const qmsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/quality',
    component: () => <L><QMS /></L>,
});

// ─── Maintenance / CMMS (staff+) ────────────────────────────────────
const maintenanceRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/maintenance',
    component: () => <L><Maintenance /></L>,
});

// ─── Karr Fleet (fleet-1; docs/KARR_FLEET_SPEC.md) ──────────────────
const fleetRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/fleet',
    component: () => <L><FleetHome /></L>,
});

// ─── Karr Mandi (mandi-1; docs/KARR_MANDI_SPEC.md) ──────────────────
const mandiRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/mandi',
    component: () => <L><MandiHome /></L>,
});

// ─── Karr Stay (stay-1; docs/KARR_STAY_SPEC.md) ─────────────────────
const stayRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/stay',
    component: () => <L><StayHome /></L>,
});

// ─── Karr Campus (campus-1; docs/KARR_SCHOOL_SPEC.md) ───────────────
const campusRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/campus',
    component: () => <L><CampusHome /></L>,
});

// ─── Karr Jewel (jewel-1; docs/KARR_JEWEL_SPEC.md) ──────────────────
const jewelRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/jewel',
    component: () => <L><JewelHome /></L>,
});

// ─── Karr Studio (studio-1; docs/KARR_STUDIO_SPEC.md) ───────────────
// Each desk its own address, so Karr's sidebar carries the vertical's list: Book at
// /studio, the other six at /studio/<desk>. studio-5: the desks are static paths, so a
// studio's own public page /studio/<name> (root, no login) never takes a desk's word.
const studioRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/studio',
    component: () => <L><StudioHome desk="book" /></L>,
});
const studioClassesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/studio/classes',
    component: () => <L><StudioHome desk="classes" /></L>,
});
const studioClientsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/studio/clients',
    component: () => <L><StudioHome desk="clients" /></L>,
});
const studioTillRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/studio/till',
    component: () => <L><StudioHome desk="till" /></L>,
});
const studioStaffRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/studio/staff',
    component: () => <L><StudioHome desk="staff" /></L>,
});
const studioMorningRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/studio/morning',
    component: () => <L><StudioHome desk="morning" /></L>,
});
const studioPageRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/studio/page',
    component: () => <L><StudioHome desk="page" /></L>,
});

// ─── Karr Service (service-1; docs/KARR_SERVICE_SPEC.md) ────────────
const serviceRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/service',
    component: () => <L><ServiceHome /></L>,
});

// ─── Dimensions (accountant+) ───────────────────────────────────────
const dimensionsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/dimensions',
    component: () => <L><Dimensions /></L>,
});

// ─── Fixed expenses (fixpage: left Settings for Books) ─────────────
const fixedExpensesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/fixed-expenses',
    component: () => <L><FixedExpenses /></L>,
});

// ─── E-commerce (staff+) ────────────────────────────────────────────
const ecommerceRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/ecommerce',
    component: () => <L><Ecommerce /></L>,
});

// ─── Documents (staff+) ─────────────────────────────────────────────
const documentsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/documents',
    component: () => <L><Documents /></L>,
});

// ─── Subscriptions (staff+) ─────────────────────────────────────────
const subscriptionsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/subscriptions',
    component: () => <L><Subscriptions /></L>,
});

// ─── Budgeting (viewer+) ────────────────────────────────────────
const budgetingRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/budgeting',
    component: () => <L><Budgeting /></L>,
});

// ─── Projections & Bank Finance (pfs-4) ─────────────────────────
const projectionsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/projections',
    component: () => <L><Projections /></L>,
});

// ─── Karr Me (me-1) ─────────────────────────────────────────────
const meRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/me',
    component: () => <L><MeHome /></L>,
});

// ─── Time Tracking (data_entry+) ────────────────────────────────
const timeTrackingRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/time-tracking',
    component: () => <L><TimeTracking /></L>,
});

// ─── Multi-Currency (viewer+) ───────────────────────────────────
const multiCurrencyRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/multi-currency',
    component: () => <L><EntitledRoute feature={ROUTE_FEATURE['/multi-currency']}><MultiCurrency /></EntitledRoute></L>,
});

// ─── Customer / Vendor Portal (admin) ───────────────────────────
const portalRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/portal',
    component: () => <L><CustomerVendorPortal /></L>,
});

// ─── AI Tools (viewer+) ─────────────────────────────────────────
const aiToolsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/ai-tools',
    component: () => <L><AiTools /></L>,
});

// ─── Compliance Intelligence (viewer+) ──────────────────────────
const complianceRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/compliance',
    component: () => <L><Compliance /></L>,
});

// ─── Modules — the activation gallery (mod-3, core route) ───────
const modulesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/modules',
    component: () => <L><Modules /></L>,
});

// ─── Marketplace — the shelf: every add-on, its price, one button (market) ──
const marketplaceRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/marketplace',
    component: () => <L><Marketplace /></L>,
});

// ─── Payables Cockpit (viewer+) — Flywheel pay-1 ────────────────
const payWeekRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/pay-week',
    component: () => <L><PayWeek /></L>,
});

// ─── Network connect accept screen (authed) — Flywheel net-3 ────
const networkConnectRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/connect/$token',
    component: () => <L><NetworkConnect /></L>,
});

// ─── The year side by side (noticeproof np-9) ─────────────────────
const yearTiesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/ties',
    component: () => <L><YearTies /></L>,
});
const yearTiesFyRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/ties/$fy',
    component: () => <L><YearTies /></L>,
});

// ─── Books Health (viewer+) — Flywheel bh-2 ─────────────────────
const booksHealthRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/books-health',
    component: () => <L><BooksHealth /></L>,
});

// ─── Notices & demands — the AFTER layer (fr-a1 / fr-a3 / fr-a4) ──
const noticesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/notices',
    component: () => <L><Notices /></L>,
});

// ─── Admin: Books Health fleet (platform-admin gated in the page) ──
const booksHealthAdminRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/admin/books-health',
    component: () => <L><BooksHealthAdmin /></L>,
});

// ─── Admin: Releases, a migration applied on a press (yourplace yp-4; platform-admin gated in the page) ──
const releasesAdminRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/admin/releases',
    component: () => <L><ReleasesAdmin /></L>,
});

// ─── Own place: DigitalOcean sends the owner back here (yourplace-easy ype-3) ──
const placeCloudReturnRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/place/cloud-return',
    component: () => <L><PlaceCloudReturn /></L>,
});

// ─── Own place: the office box's card opens this (yourplace-easy ype-5) ──
const boxClaimRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/box/$claim',
    component: () => <L><BoxClaim /></L>,
});

// ─── Admin: Own places, the automatic moves and Karr's press (yourplace-easy ype-2; platform-admin gated in the page) ──
const placesAdminRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/admin/places',
    component: () => <L><PlacesAdmin /></L>,
});

// ─── Admin: Customers, sign-ups, renewals, activity (signdesk; gated in the page) ──
const customersAdminRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/admin/customers',
    component: () => <L><CustomersAdmin /></L>,
});

// ─── Admin Console hub (platform-admin gated in the page) ──
const adminHomeRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/admin',
    component: () => <L><AdminHome /></L>,
});

// ─── Admin: Compliance Rule Studio (platform-admin gated in the page) ──
const complianceAdminRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/admin/compliance',
    component: () => <L><ComplianceRuleAdmin /></L>,
});

// ─── Admin: Error Log (platform-admin gated in the page) ──
const errorLogRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/admin/errors',
    component: () => <L><ErrorLog /></L>,
});

// ─── Admin: Ideas from users, from Improve Karr (improve; gated in the page) ──
const ideasAdminRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/admin/ideas',
    component: () => <L><IdeasAdmin /></L>,
});

// ─── Your Karr Accounting Score, from the profile menu (pm-17) ──
const accountingScoreRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/learn',
    component: () => <L><AccountingScore /></L>,
});

// ─── Loan Management (viewer+) ──────────────────────────────────
const loansRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/loans',
    component: () => <L><LoanManagement /></L>,
});

// ─── CRM (viewer+) ──────────────────────────────────────────────
const crmRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/crm',
    component: () => <L><CRM /></L>,
});

// ─── Task Management (viewer+) ──────────────────────────────────
const tasksRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/tasks',
    component: () => <L><TaskManagement /></L>,
});

// ─── Portfolio Cockpit (multi-entity command centre) ────────────
const portfolioRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/portfolio',
    component: () => <L><PortfolioCockpit /></L>,
});

// ─── Portfolio → one client's compliance page (cc-4) ────────────
const portfolioClientRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/portfolio/client/$orgId',
    component: () => <L><PortfolioClient /></L>,
});

// ─── My Work (practice staff employee-first landing) ────────────
const myWorkRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/my-work',
    component: () => <L><MyWork /></L>,
});

// ─── Document Requests (PBC loop) ───────────────────────────────
const requestsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/requests',
    component: () => <L><DocumentRequests /></L>,
});

// ─── Product codes (hsnsweep) ───────────────────────────────────
// Every broken HSN/SAC across every business the person works on.
const productCodesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/product-codes',
    component: () => <L><ProductCodes /></L>,
});

// ─── Audit Trail (governance) ───────────────────────────────────
const auditTrailRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/audit-trail',
    component: () => <L><AuditTrail /></L>,
});

// ─── Audit: which audit this business owes, where it stands, the one next thing ──
// One screen of chapters (auditflow, docs/AUDIT_FLOW_SPEC.md). Closing the
// year, the engagement and the 3CD are chapters of it, so the two addresses
// they used to have are redirects below, never sidebar leaves.
const auditRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/audit',
    component: () => <L><Audit /></L>,
});

// ─── The engagement desk, retired into Audit (auditflow A4) ──
// The address stays for bookmarks and stored fix buttons; it carries `fy`,
// `org` and `tab=signing` (as `chapter=sign`) across to `/audit`.
const auditDeskRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/audit-desk',
    component: () => <L><AuditRedirect /></L>,
});

// ─── The firm's own portfolio, across every client it audits (aud-10) ──
const myAuditsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/my-audits',
    component: () => <L><MyAudits /></L>,
});

// ─── Close & Lock Periods (year-end close + soft month locks) ───
const periodLocksRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/period-locks',
    component: () => <L><PeriodLocks /></L>,
});

// ─── Practice CRM (client lifecycle + comms) ────────────────────
const practiceCrmRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/practice-crm',
    // crmlist-2: `?client=<id>` opens that client's record (Ctrl+K lands here).
    validateSearch: (s: Record<string, unknown>): { client?: string } =>
        (typeof s.client === 'string' && s.client ? { client: s.client } : {}),
    component: () => <L><PracticeCRM /></L>,
});

// ─── Practice (retired) → redirect to the Practice CRM ──────────
const practiceRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/practice',
    component: () => <Navigate to="/practice-crm" />,
});

// ─── Partner Program (referral commissions) ─────────────────────
const partnerRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/partner',
    component: () => <L><PartnerProgram /></L>,
});

// ─── Notifications (bell "View All" target) ─────────────────────
const notificationsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/notifications',
    component: () => <L><Notifications /></L>,
});

// ─── GST & TDS (Sandbox-powered ledgers/returns/TDS) ────────────
const gstTdsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/gst-tds',
    component: () => <L><GstTds /></L>,
});

// ─── GST Filing Cockpit (P2-9 — evidence-gated one-click filing) ──
// ─── Software exports (sx-3 — SOFTEX/EDF under FEMA) ────────────
const softexRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/softex',
    component: () => <L><Softex /></L>,
});

const gstFilingRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/gst-filing',
    component: () => <L><GstFiling /></L>,
});

// ─── ITR Filing Cockpit (P2-10 — income-tax return compute → checks → file) ──
const itrFilingRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/itr-filing',
    component: () => <L><ItrFiling /></L>,
});

// ─── Advance tax (section 211 instalments, 234B/234C interest at risk) ──
// The client's own income-tax planning for the running year. It used to be a
// tab of the tax-audit page; it is not the audit, so it has its own door.
const advanceTaxRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/advance-tax',
    component: () => <L><AdvanceTax /></L>,
});

// ─── TDS deposit cockpit (tf-2 — the monthly withholding loop) ──
const tdsFilingRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/tds-filing',
    component: () => <L><TdsFiling /></L>,
});

// ─── Payroll statutory filing cockpit (psf-1 — PF / ESI / PT / LWF returns) ──
const payrollFilingRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/payroll-filing',
    component: () => <L><PayrollFiling /></L>,
});

// ─── Statutory Registers (Companies Act 2013 — members/directors) ──
const statutoryRegistersRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/statutory-registers',
    // dsccols: Office > DSC opens one person here, by PAN, DIN or name.
    validateSearch: (q: Record<string, unknown>): { pan?: string; din?: string; name?: string } => ({
        ...(typeof q.pan === 'string' ? { pan: q.pan } : {}),
        ...(typeof q.din === 'string' ? { din: q.din } : {}),
        ...(typeof q.name === 'string' ? { name: q.name } : {}),
    }),
    component: () => <L><StatutoryRegisters /></L>,
});

// ─── ROC Compliance (Companies Act 2013 journey — entitlement-gated in-page) ──
const rocRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/roc',
    component: () => <L><RocJourney /></L>,
});

// ─── Karr Copilot: get a filing by the CIN typed on an MCA form (copilotcin) ──
// ─── adt1-6: the SRN of an ADT-1 / ADT-3 comes home from MCA's acknowledgement ──
const copilotFiledRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/copilot/filed',
    component: () => <L><CopilotFiled /></L>,
});

const copilotGetRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/copilot/get',
    component: () => <L><CopilotGet /></L>,
});

// ─── Karr Lab — LIS vertical (entitlement-gated: feature:lab) ──
const labHomeRoute = createRoute({
    // hf-6: forwards from the root, so an old link never mounts the accounting shell first.
    getParentRoute: () => rootRoute,
    path: '/lab',
    // hms-5: the lab is a desk of Karr HMS now; an old link lands on it.
    component: () => <Navigate to="/health/lab" />,
});

// ─── Karr Health (hf-6; hf-fix-3, law 2): /health and its desks, in Karr's own shell ──
// The old /hms addresses (hms-3..32) forward to the same page under /health:
// a bookmark, a printed QR code or a link in a WhatsApp still lands.
const hmsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/hms',
    component: () => <Navigate to="/health" replace />,
});
const hmsDeskRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/hms/$desk',
    component: function HmsForward() {
        const { desk } = hmsDeskRoute.useParams();
        return <Navigate to={`/health/${desk}`} replace />;
    },
});
// docs/VERTICALS_ROADMAP.md §1, law 2: a vertical keeps AppLayout and changes only the
// sidebar's list. HealthArea hands the person's desks to the sidebar, lands them on their
// own first screen, and keeps the hospital's idle lock and ward-tablet switch.
const healthLayoutRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    id: '_healthArea',
    component: function HealthAreaRoute() {
        return (
            <Suspense fallback={<ContentFallback />}>
                <HealthArea />
            </Suspense>
        );
    },
});
const healthRoute = createRoute({
    getParentRoute: () => healthLayoutRoute,
    path: '/health',
    component: () => <L><HmsHome /></L>,
});
const healthDeskRoute = createRoute({
    getParentRoute: () => healthLayoutRoute,
    path: '/health/$desk',
    component: () => <L><HmsHome /></L>,
});
// hmsux A-1: the visit and its bill, full screen, Back to where it was opened.
const healthVisitRoute = createRoute({
    getParentRoute: () => healthLayoutRoute,
    path: '/health/visit/$visitId',
    component: () => <L><HmsVisitScreen /></L>,
});
// hf-3: an owner or admin registers this tablet; in Karr's shell since hf-fix-3 (it had its own page and Back).
const healthDeviceRoute = createRoute({
    getParentRoute: () => healthLayoutRoute,
    path: '/health/device',
    component: () => <L><HealthDevice /></L>,
});
// hf-4: the hospital's people (owners, admins, HR).
const healthPeopleRoute = createRoute({
    getParentRoute: () => healthLayoutRoute,
    path: '/health/people',
    component: () => <L><HealthPeople /></L>,
});
// hf-15: the duty roster.
const healthRosterRoute = createRoute({
    getParentRoute: () => healthLayoutRoute,
    path: '/health/roster',
    component: () => <L><HealthRoster /></L>,
});

// ─── Karr Travel (travel-3; ts-2; law 2): /travel in Karr's own shell ─────────
// docs/VERTICALS_ROADMAP.md §1, law 2: a vertical keeps AppLayout and changes
// only the sidebar's list. TravelArea hands the person's travel places to the
// sidebar, lands them on their own first screen and shows a ringing call.
const travelLayoutRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    id: '_travelArea',
    component: function TravelAreaRoute() {
        return (
            <Suspense fallback={<ContentFallback />}>
                <TravelArea />
            </Suspense>
        );
    },
});
const travelRoute = createRoute({
    getParentRoute: () => travelLayoutRoute,
    path: '/travel',
    component: () => <L><TravelHome /></L>,
});
// ts-2: head office's control room (and the wall screen, ?tv=1).
const travelHqRoute = createRoute({
    getParentRoute: () => travelLayoutRoute,
    path: '/travel/hq',
    component: () => <L><TravelHq /></L>,
});
// ts-6: the travel company's own door (no login) and the PIN a person chooses.
const travelSignInRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/travel/signin',
    component: () => <L><TravelSignIn /></L>,
});
const travelPinRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/travel/pin',
    component: function ProtectedTravelPin() {
        const { session, initialized, loading } = useAuth();
        if (loading) return null;
        if (!session && initialized) return <Navigate to="/travel/signin" />;
        return <L><TravelPin /></L>;
    },
});
// ts-6: counter tablets and wall screens (owners, admins, head office, branch heads).
const travelDevicesRoute = createRoute({
    getParentRoute: () => travelLayoutRoute,
    path: '/travel/devices',
    component: () => <L><TravelDevices /></L>,
});
// ts-4: the company's travel people (owners, admins, head office, branch heads).
const travelPeopleRoute = createRoute({
    getParentRoute: () => travelLayoutRoute,
    path: '/travel/people',
    component: () => <L><TravelPeople /></L>,
});
const travelDeskRoute = createRoute({
    getParentRoute: () => travelLayoutRoute,
    path: '/travel/$desk',
    component: () => <L><TravelHome /></L>,
});
const travelTripRoute = createRoute({
    getParentRoute: () => travelLayoutRoute,
    path: '/travel/trip/$tripId',
    component: () => <L><TravelHome /></L>,
});

// ─── Receivables & Payables (viewer+) ─────────────────────────
const receivablesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/receivables',
    component: () => <L><Receivables /></L>,
});

// ─── Compliance Reports (viewer+) ───────────────────────────────
const complianceReportsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/compliance-reports',
    component: () => <L><ComplianceReports /></L>,
});

// ─── The 3CD page, retired into Audit (auditflow A4) ──
// The 3CD is a chapter of `/audit` and advance tax is `/advance-tax`. The
// address stays for bookmarks and stored fix buttons and carries `fy` and
// `org` across.
const taxAuditRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/tax-audit',
    component: () => <L><AuditRedirect /></L>,
});

// ─── Accrual Cockpit / Month-end (ga-3 — viewer+) ──
const accrualsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/accruals',
    component: () => <L><AccrualCockpit /></L>,
});

// ─── My Businesses — owner group dashboard (ga-8) ──
const myBusinessesRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/my-businesses',
    component: () => <L><MyBusinesses /></L>,
});

// ─── NGO Reports (viewer+) ──────────────────────────────────────
const ngoReportsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/ngo-reports',
    component: () => <L><NGOReports /></L>,
});

// ─── Donations & receipts (viewer+) ─────────────────────────────
const clientSheetRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/client-sheet',
    // ?org=<id> opens a client from the switcher's list without switching to it.
    validateSearch: (s: Record<string, unknown>): { org?: string } => (typeof s.org === 'string' ? { org: s.org } : {}),
    component: () => <L><ClientSheet /></L>,
});

const officeCredentialsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/office-credentials',
    component: () => <L><OfficeCredentials /></L>,
});

// dsc: every client's DSCs, where each token is, and what to start this week.
const officeDscRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/office-dsc',
    component: () => <L><OfficeDsc /></L>,
});

// fileready fr-3: what every client's filings still need, as one sheet.
const officeFilingsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/office-filings',
    component: () => <L><OfficeFilings /></L>,
});

// officetds ot-2: every client's TDS quarter, as one sheet.
const officeTdsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/office-tds',
    component: () => <L><OfficeTds /></L>,
});

// practice-1: Karr Practice's Matters / Projects desk. `/practice` itself
// stays the old redirect to the CRM, so a CA firm's bookmarks still land.
const practiceMattersRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/practice/matters',
    component: () => <L><PracticeMatters /></L>,
});

const donationsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/donations',
    component: () => <L><Donations /></L>,
});

// ─── Integrations: every connection as a tile (connect-1) ───────
const integrationsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/integrations',
    component: () => <L><Integrations /></L>,
});

// connect-3: the ERPNext wizard over the Integrations page; ?step= lands on a step.
const integrationsErpnextRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/integrations/erpnext',
    component: () => <L><Integrations erp /></L>,
});

// ─── Developers: keys, webhooks, the documents door, the API (connect-2) ──
const developersRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/developers',
    component: () => <L><Developers /></L>,
});

// The two pages Developers replaced; an old link or bookmark still lands.
const apiSettingsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/api-settings',
    component: () => <Navigate to="/developers" replace />,
});
const apiDocsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/api-docs',
    component: () => <Navigate to="/developers" hash="docs" replace />,
});

// ─── Report Builder (viewer+) ───────────────────────────────────
const reportBuilderRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/report-builder',
    component: () => <L><EntitledRoute feature={ROUTE_FEATURE['/report-builder']}><ReportBuilder /></EntitledRoute></L>,
});

// ─── Consolidation (admin) ──────────────────────────────────────
const consolidationRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/consolidation',
    component: () => <L><EntitledRoute feature={ROUTE_FEATURE['/consolidation']}><Consolidation /></EntitledRoute></L>,
});

// ─── Import Data / Migration Engine (admin) ─────────────────────
const importDataRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/import',
    component: () => <L><ImportData /></L>,
});

// ─── Import from Tally (tly-3): the one door, six steps ──────────
const tallyImportRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/import/tally',
    component: () => <L><TallyImport /></L>,
});

// ─── Import from Vyapar (vyapar): the backup read on the device, then Tally's six steps ──
const vyaparImportRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/import/vyapar',
    component: () => <L><TallyImport source="Vyapar" /></L>,
});

// ─── Invoices from another system (erpin-2): a file through the ERP's door ──
const documentImportRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/import/documents',
    component: () => <L><DocumentImport /></L>,
});

// ─── Sales from selling platforms (mktin-1): a platform's report through the same door ──
const platformImportRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/import/platforms',
    component: () => <L><PlatformImport /></L>,
});

// ─── Connected platforms (payin-1): the review list, and the page a platform's sign-in returns to ──
const platformReviewRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/platforms/review',
    component: () => <L><PlatformReview /></L>,
});
// payin-3: remittance advices, and every export with its money and its clock
const platformFiraRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/platforms/fira',
    component: () => <L><PlatformFira /></L>,
});
const platformExportsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/platforms/exports',
    component: () => <L><PlatformExports /></L>,
});
// payin-10: the GST refund on exports
const gstRefundRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/gst/refund',
    component: () => <L><GstRefund /></L>,
});
// payin-5: the Subscriptions board
const platformSubscriptionsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/platforms/subscriptions',
    component: () => <L><PlatformSubscriptions /></L>,
});
// payin-7: statements by file, and the mail forwarded to the business's address
const platformStatementRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/platforms/statement',
    component: () => <L><PlatformStatement /></L>,
});
const platformInboxRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/platforms/inbox',
    component: () => <L><PlatformInbox /></L>,
});
const connectDoneRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/connect/$platform/done',
    component: () => <L><ConnectDone /></L>,
});

// ─── /import/vouchers: retired, and it sends you to its replacement ──
// The old page read a Day Book and posted every voucher as a journal. That is
// now step 5 of /import/tally, which also reads the ledgers, remembers where
// each one goes, and ties to Tally before it calls the import done. The route
// stays so a bookmark still lands somewhere real.
const voucherImportRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/import/vouchers',
    component: () => <L><VoucherImport /></L>,
});

// ─── Collaboration (viewer+) ────────────────────────────────────
const collaborationRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/collaboration',
    component: () => <L><Collaboration /></L>,
});

// ─── Payment Links (staff+) ────────────────────────────────────
const paymentLinksRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/payment-links',
    component: () => <L><EntitledRoute feature={ROUTE_FEATURE['/payment-links']}><PaymentLinks /></EntitledRoute></L>,
});

// ─── Email Integration (admin) ──────────────────────────────────
const emailRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/email',
    component: () => <L><EmailIntegration /></L>,
});

// ─── Email OAuth Callback (authenticated) ───────────────────────
const emailOAuthCallbackRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/email/oauth/callback',
    component: () => <L><EmailOAuthCallback /></L>,
});

// ─── Receipt Scanner (staff+) ──────────────────────────────────
const receiptScannerRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/receipt-scanner',
    component: () => <L><ReceiptScanner /></L>,
});

// practice-12: everything shared to Karr from a phone lands here first.
const shareRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/share',
    component: () => <L><Share /></L>,
});

// ─── Bank Feeds (admin) ────────────────────────────────────────
const bankFeedsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/bank-feeds',
    component: () => <L><EntitledRoute feature={ROUTE_FEATURE['/bank-feeds']}><BankFeeds /></EntitledRoute></L>,
});

// ─── AI Settings → the AI tab of Settings ───────────────────────
// The page moved into Settings (16 Sep 2026). The address stays because fix
// buttons, the sidebar, the AI chat and the marketplace all name it.
const aiSettingsConfigRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/ai-settings',
    component: () => <Navigate to="/settings" hash="ai" replace />,
});

// ─── Products → Items redirect ──────────────────────────────────
const productsRedirectRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/products',
    component: () => <Navigate to="/items" />,
});

// ─── Settings (admin) ───────────────────────────────────────────
const settingsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/settings',
    component: () => <L><SettingsPage /></L>,
});

// aiconsent: a client reads the whole notice and decides (docs/AI_CONSENT_SPEC.md §6).
const aiAccessConsentRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/settings/ai-access/$id',
    component: () => <L><AiAccessConsent /></L>,
});
const aiAccessRecordRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/settings/ai-access/record/$id',
    component: () => <L><AiAccessRecord /></L>,
});

// ─── Karr Agents Management (admin) ─────────────────────────────
const karrAgentsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/karr-agents',
    component: () => <L><KarrAgents /></L>,
});

// ═══════════════════════════════════════════════════════════════
// Route Tree Assembly
// ═══════════════════════════════════════════════════════════════
// Public routes (landing, auth, onboarding) → direct children of root
// All app pages → children of appLayoutRoute (persistent sidebar/header)
// ═══════════════════════════════════════════════════════════════

// ─── Fixed assets lane C: the year end, the files, the count (docs/FIXED_ASSETS_SPEC.md §8, §9) ──
const FixedAssetStatements = lazy(() => import('./routes/fixedAssets/StatementsPage'));
const fixedAssetStatementsRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/fixed-assets/statements',
    component: () => <L><FixedAssetStatements /></L>,
});
const FixedAssetImport = lazy(() => import('./routes/fixedAssets/Import'));
const fixedAssetImportRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/fixed-assets/import',
    component: () => <L><FixedAssetImport /></L>,
});
const FixedAssetVerify = lazy(() => import('./routes/fixedAssets/Verify'));
const FixedAssetVerifySession = lazy(() => import('./routes/fixedAssets/VerifySession'));
const AssetTag = lazy(() => import('./routes/fixedAssets/AssetTag'));
const fixedAssetVerifyRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/fixed-assets/verify',
    component: () => <L><FixedAssetVerify /></L>,
});
const fixedAssetVerifySessionRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/fixed-assets/verify/$sessionId',
    component: () => <L><FixedAssetVerifySession /></L>,
});
// A tag's QR code: /a/<tag>, a sign-in required (docs/FIXED_ASSETS_SPEC.md §9).
const assetTagRoute = createRoute({
    getParentRoute: () => appLayoutRoute,
    path: '/a/$tag',
    component: () => <L><AssetTag /></L>,
});

const appRoutes = appLayoutRoute.addChildren([
    dashboardRoute,
    accountsRoute, openingBalancesRoute, getStartedRoute, contactsRoute, itemsRoute, journalsRoute,
    salesRoute, estimatesRoute, creditNotesRoute, deliveryChallansRoute, recurringInvoicesRoute,
    purchasesRoute, purchaseOrdersRoute, debitNotesRoute, grnRoute, recurringBillsRoute,
    expensesRoute,
    fixedAssetStatementsRoute, fixedAssetImportRoute, fixedAssetVerifyRoute, fixedAssetVerifySessionRoute, assetTagRoute,
    moneyInRoute, moneyOutRoute, transfersRoute,
    reportsRoute, gstReportsRoute, bankReconciliationRoute, bankStatementsRoute,
    fixedAssetsRoute, addFixedAssetRoute, assetJourneyRoute, payrollRoute, inventoryRoute, serialLifeRoute, manufacturingRoute, approvalsRoute, salesOrdersRoute, projectsRoute, posRoute, restaurantRoute, kdsRoute, foodCostRoute, aggregatorsRoute, warehouseOpsRoute, rfqRoute, hrRoute, helpdeskRoute, qmsRoute, maintenanceRoute, dimensionsRoute, fixedExpensesRoute, ecommerceRoute, documentsRoute, subscriptionsRoute, budgetingRoute, projectionsRoute, meRoute, fleetRoute, mandiRoute, stayRoute, campusRoute, jewelRoute, studioRoute, studioClassesRoute, studioClientsRoute, studioTillRoute, studioStaffRoute, studioMorningRoute, studioPageRoute, serviceRoute,
    timeTrackingRoute, multiCurrencyRoute, buildRoute,
    portalRoute, aiToolsRoute, complianceRoute, modulesRoute, marketplaceRoute, payWeekRoute, networkConnectRoute, booksHealthRoute, yearTiesRoute, yearTiesFyRoute, noticesRoute, booksHealthAdminRoute, releasesAdminRoute, placesAdminRoute, placeCloudReturnRoute, boxClaimRoute, customersAdminRoute, adminHomeRoute, complianceAdminRoute, errorLogRoute, ideasAdminRoute, accountingScoreRoute, loansRoute, crmRoute,
    tasksRoute, portfolioRoute, portfolioClientRoute, myWorkRoute, practiceCrmRoute, partnerRoute, notificationsRoute, gstTdsRoute, gstFilingRoute, softexRoute, itrFilingRoute, tdsFilingRoute, payrollFilingRoute, requestsRoute, productCodesRoute, practiceRoute, auditTrailRoute, auditRoute, auditDeskRoute, myAuditsRoute, periodLocksRoute, receivablesRoute, statutoryRegistersRoute, rocRoute, copilotGetRoute, copilotFiledRoute,
    complianceReportsRoute, ngoReportsRoute, donationsRoute, officeCredentialsRoute, officeDscRoute, officeFilingsRoute, officeTdsRoute, practiceMattersRoute, clientSheetRoute, taxAuditRoute, advanceTaxRoute, accrualsRoute, myBusinessesRoute,
    integrationsRoute, integrationsErpnextRoute, developersRoute, apiSettingsRoute, apiDocsRoute, reportBuilderRoute, consolidationRoute,
    importDataRoute, tallyImportRoute, vyaparImportRoute, documentImportRoute, platformImportRoute, platformReviewRoute, platformFiraRoute, platformExportsRoute, platformSubscriptionsRoute, gstRefundRoute, platformStatementRoute, platformInboxRoute, connectDoneRoute, voucherImportRoute, collaborationRoute, paymentLinksRoute,
    emailOAuthCallbackRoute, emailRoute,
    receiptScannerRoute, shareRoute, bankFeedsRoute, aiSettingsConfigRoute, settingsRoute, aiAccessConsentRoute, aiAccessRecordRoute,
    productsRedirectRoute, karrAgentsRoute,
    // law 2: the travel pages, in this shell.
    travelLayoutRoute.addChildren([travelRoute, travelHqRoute, travelPeopleRoute, travelDevicesRoute, travelDeskRoute, travelTripRoute]),
    // hf-fix-3: and the Health pages.
    healthLayoutRoute.addChildren([healthRoute, healthDeskRoute, healthVisitRoute, healthPeopleRoute, healthRosterRoute, healthDeviceRoute]),
]);

// ─── The site (sitetour, docs/SITE_TOUR_SPEC.md) ──────────────────
// At the root since the founder's word (sitetour-12, 4 Oct 2026); `/next/*`
// redirects. Its routes are declared in src/site/routes.tsx, one per country
// page, every page a lazy chunk; `/` itself is the landing route above. The
// referral welcome reads the database, which the site may not import, so it
// is handed in here.
const siteRoutes = buildSiteRoutes(rootRoute, { HomeExtras: ReferralWelcomeModal });

const routeTree = rootRoute.addChildren([
    landingRoute,
    ...siteRoutes,
    appRoutes,
    onboardingRoute,
    createPracticeRoute,
    signUpRoute,
    signInRoute,
    setPasswordRoute,
    resetPasswordRoute,
    inviteRoute,
    employeeInviteRoute,
    offerRoute,
    myWorkplaceRoute,
    shortLinkRoute,
    gstReconnectRoute,
    auditorConfirmRoute,
    gstApproveRoute, tdsApproveRoute, tdsCsiOtpRoute, tdsChallanUploadRoute, shareCertificateOpenRoute, signCertificatesRoute,
    verifyExecutionRoute, verifyExecutionTokenRoute, signByLinkRoute, signatureByLinkRoute, meetByLinkRoute,
    gstPacketReviewRoute,
    givePageRoute,
    proposalPageRoute,
    hmsBookingRoute,
    healthSignInRoute, healthPinRoute, healthDisplayRoute, healthDisplayCodeRoute, healthSsoRoute,
    // hf-6: the old /hms addresses forward; the Health pages are in the app layout (hf-fix-3, law 2).
    hmsRoute, hmsDeskRoute, labHomeRoute,
    // ts-6: the travel company's own door; its pages are in the app layout (law 2).
    travelSignInRoute, travelPinRoute,
    travelAskRoute,
    travelLandingRoute,
    travelBrandLandingRoute,
    travelAgentRoute, travelAgentDeepRoute, travelCorporateBookRoute, travelGoRoute, platformStatusRoute, securityDisclosureRoute,
    hmsPatientDocRoute,
    applyRoute,
    stayPublicRoute,
    stayBookingRoute,
    studioPublicRoute,
    studioBookingRoute,
    studioDoorRoute,
    lendPackRoute,
    shareRoomRoute,
    clientChatRoute,
    itrApproveRoute,
    partnersRoute,
    legalRoute,
    portalLoginRoute,
]);

export const router = createRouter({
    routeTree,
    // A stale code-split route chunk after a deploy is caught by TanStack Router's
    // per-route boundary — RouteError detects it and reloads to the fresh build.
    defaultErrorComponent: ({ error }) => <RouteError error={error} />,
    // An unknown URL is usually a link minted by a NEWER build hitting a stale
    // cached shell (a client's /r/<slug> the day short links shipped) — reload
    // once for the fresh build before daring to say "not found".
    defaultNotFoundComponent: () => <SelfHealingNotFound />,
});

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
}
